import { useEffect, useRef } from 'react';
import { kbDebugEnabled, kbLog } from '../../utils/ui/keyboardReframeDebug';
import { takeOverInlineReveal } from '../../utils/ui/inlineKeyboardGuard';
import { isInlineSpacerHeld, inlineSpacerScroller } from '../../utils/ui/inlineKeyboardSpacer';
import { revealScrollerFor as nearestScrollableAncestor, isInsideModalPanel } from '../../utils/ui/modalRevealScroller';

/**
 * Keyboard "reveal the focused field" choreography for AnimatedModalShell.
 * Extracted 2026-08-20 (500-line cap) after three device-log iterations:
 *
 *  1. Always-scrolling moved already-visible fields ~49px and the offset
 *     LINGERED after the keyboard closed → "top of the modal is cut off".
 *  2. A viewport-only visibility check skipped a field hidden under the
 *     modal's sticky footer → "the focus area is not the type box".
 *  3. An exact-edge comparison re-triggered on a field FLUSH with the visible
 *     edge (bottom 385.2 vs region 385.2 — sub-pixel), and a legitimate
 *     reveal scroll was never undone, leaving the body scrolled at rest.
 *
 * Rules, in order:
 *  - Visibility = the field's rect inside the intersection of the keyboard
 *    band AND its own scroll container's box, with REVEAL_TOLERANCE_PX slack.
 *  - Reveal fires once per keyboard-open, 250ms after the reframe settles.
 *  - On keyboard close, if WE scrolled the container and the user has not
 *    scrolled it since, smooth-scroll it back to where it was — reveal is a
 *    loan, not a mutation.
 */

/** Sub-pixel/edge slack: a field flush with the visible edge IS visible. */
export const REVEAL_TOLERANCE_PX = 2;
/** Wait for the keyboard reframe (250ms curve) before measuring. */
const REVEAL_DELAY_MS = 250;
/** When our smooth reveal scroll is considered settled (measure landing spot). */
const REVEAL_SETTLE_MS = 420;
/** User-scroll tolerance: beyond this from our landing spot = user's intent. */
const RESTORE_DRIFT_PX = 8;

/** Pure visibility check (unit-tested): inside the region, with slack. */
export function isRevealNeeded(rect, regionTop, regionBottom, tol = REVEAL_TOLERANCE_PX) {
  return !(rect.top >= regionTop - tol && rect.bottom <= regionBottom + tol);
}

export default function useKeyboardFieldReveal(keyboardOpen) {
  // { scroller, beforeTop, afterTop, settled, settleTimer } for the last
  // reveal WE performed — the restore contract on close.
  const revealRef = useRef(null);

  useEffect(() => {
    if (!keyboardOpen) {
      const r = revealRef.current;
      revealRef.current = null;
      if (r) clearTimeout(r.settleTimer);
      // Restore only a SETTLED reveal the user hasn't overridden: unsettled
      // (fast close) means our scroll may not have run; drift beyond
      // RESTORE_DRIFT_PX means the user scrolled on purpose — keep their spot.
      if (
        r && r.settled && r.scroller
        && r.afterTop !== r.beforeTop
        && Math.abs(r.scroller.scrollTop - r.afterTop) <= RESTORE_DRIFT_PX
      ) {
        kbLog('revealRestore', { from: r.afterTop, to: r.beforeTop });
        r.scroller.scrollTo({ top: r.beforeTop, behavior: 'smooth' });
      }
      return undefined;
    }

    const timer = setTimeout(() => {
      const el = document.activeElement;
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA' && el.tagName !== 'SELECT')) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const vv = window.visualViewport;
      let regionTop = (vv ? vv.offsetTop : 0) + 8;
      let regionBottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 8;
      const scroller = nearestScrollableAncestor(el);
      // Field in a modal panel's pinned band: the modal's own lift/curtain owns
      // it. scrollIntoView would scroll the panel (every ancestor) — never.
      if (!scroller && isInsideModalPanel(el)) return;
      if (scroller) {
        const sr = scroller.getBoundingClientRect();
        regionTop = Math.max(regionTop, sr.top);
        regionBottom = Math.min(regionBottom, sr.bottom);
      }
      if (!isRevealNeeded(rect, regionTop, regionBottom)) {
        kbLog('scrollIntoView', { skip: 1, top: rect.top, bottom: rect.bottom, rt: regionTop, rb: regionBottom });
        return;
      }
      kbLog('scrollIntoView', { tag: el.tagName, top: rect.top, bottom: rect.bottom, rt: regionTop, rb: regionBottom });
      // If the global inline guard already raised this scroller at focusin but
      // could only PARTIALLY clear the field (headroom-limited), take its loan
      // over — otherwise we'd record its raised position as "before" and the
      // two restore chains would fight over one scroller. See
      // takeOverInlineReveal for the full reasoning.
      const handoff = scroller ? takeOverInlineReveal(scroller) : null;
      const beforeTop = scroller ? (handoff ? handoff.beforeTop : scroller.scrollTop) : null;
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      // The modal close choreography owns this scroller's scroll-back (it HOLDS
      // the spacer): recording a second restore here would fight it.
      const ownedByModalClose = scroller != null && isInlineSpacerHeld() && inlineSpacerScroller() === scroller;
      if (scroller != null && !ownedByModalClose) {
        const record = { scroller, beforeTop, afterTop: beforeTop, settled: false, settleTimer: 0 };
        record.settleTimer = setTimeout(() => {
          record.afterTop = scroller.scrollTop;
          record.settled = true;
        }, REVEAL_SETTLE_MS);
        revealRef.current = record;
      }
    }, REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [keyboardOpen]);
}

/**
 * kbdebug ground-truth sampler: while the keyboard frame is open, record the
 * dialog's ACTUAL on-screen rect every animation frame (when it moved ≥0.5px).
 * Whatever mechanism moves the modal, the rect can't lie. No-op unless the
 * kbdebug flag is on.
 */
export function useKbDebugRectSampler(keyboardOpen, dialogEl) {
  useEffect(() => {
    if (!kbDebugEnabled() || !keyboardOpen || !dialogEl) return undefined;
    let rafId = 0;
    let last = { top: NaN, h: NaN };
    const sample = () => {
      const r = dialogEl.getBoundingClientRect();
      if (Math.abs(r.top - last.top) >= 0.5 || Math.abs(r.height - last.h) >= 0.5) {
        last = { top: r.top, h: r.height };
        kbLog('rect', { top: r.top, h: r.height });
      }
      rafId = requestAnimationFrame(sample);
    };
    rafId = requestAnimationFrame(sample);
    return () => cancelAnimationFrame(rafId);
  }, [keyboardOpen, dialogEl]);
}
